import { Component, Element, h, Host, Prop } from '@stencil/core';

import { BoxRoundedPreset } from '../../../services/interfaces';
import { getCSSVariable } from '../../../services/utils';

@Component({
  tag: 'pk-data-card',
  styleUrl: './scss/pk-data-card.scss',
  shadow: true,
})
export class PkDataCard {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkDataCardElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Specify a valid background color token to be used on the header. If used with bgImage, bgImage takes precedence.*/
  @Prop() bgColor: string = 'elevation-01';

  /** Specify a background image to be used on the header.*/
  @Prop() bgImage: string;

  /** Specify a card width */
  @Prop() width: string | number;

  /** Specify a card height */
  @Prop() height: string | number = '100%';

  /** Specify the value of the corner radius
   * (it should be a valid Panenka --border-radius token value) */
  @Prop({ reflect: true }) rounded: BoxRoundedPreset = 'xl';

  /** If `true`, a box shadow will applied to the box container */
  @Prop({ reflect: true }) shadow: boolean = false;

  /** If `false`, the stroke border style will be removed */
  @Prop({ reflect: true }) stroke: boolean = true;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.width && { '--data-card--width': `${this.width}` }),
      ...(this.height && { '--data-card--height': `${this.height}` }),
      ...(this.rounded && { '--data-card--border-radius': getCSSVariable(`border-radius-${this.rounded}`) }),
    };

    const headerStyle = {
      ...(this.bgColor && {
        '--data-card--background-color': getCSSVariable(this.bgColor),
      }),
      ...(this.bgImage && {
        '--data-card--background-image': `${this.bgImage}`,
      }),
    };

    return (
      <Host style={{ ...style }}>
        <div
          class={{
            'pk-data-card__outline': true,
            'has-shadow': this.shadow,
            'has-stroke': this.stroke,
          }}
          part="base"
        >
          <div
            class="bg-[color:--data-card--background-color] bg-[image:--data-card--background-image] bg-[length:100%] bg-[position:center_top] px-m pb-0 pt-m leading-m text-text-01"
            part="header"
            style={{ ...headerStyle }}
          >
            <slot name="header" />
          </div>
          <div class="grow px-m pb-m pt-0" part="body">
            <slot name="body" />
          </div>
          <div class="cta-container flex items-end px-m pb-m pt-0" part="cta">
            <slot name="cta" />
          </div>
        </div>
      </Host>
    );
  }
}
